import React, { useEffect, useState } from 'react';
import './Hero.css';
import logoImg from '../purayida-krishi-logo-malayalam.png';
import { apiGet } from '../api';

const Hero = ({ language }) => {
  const [bgImage, setBgImage] = useState('/pbg.jpg');
  const [trending, setTrending] = useState(null);
  const [recent, setRecent] = useState(null);
  
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const data = await apiGet('/api/settings');
        if (!ignore && data?.heroBackgroundUrl) {
          setBgImage(data.heroBackgroundUrl);
        }
      } catch (_) {
        // Use default background if API fails
      }
    })();
    return () => { ignore = true; };
  }, []);

  // Load dynamic posts for discover boxes
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        // Read settings to see if admin specified custom slugs
        const settings = await apiGet('/api/settings');
        const box2Slug = settings?.heroBox2Slug || null;
        const box3Slug = settings?.heroBox3Slug || null;

        // Resolve Box 2 (Trending/Custom)
        if (box2Slug) {
          try {
            // Force Malayalam for admin-selected hero boxes
            const p = await apiGet(`/api/posts/${encodeURIComponent(box2Slug)}`, { locale: 'ml' });
            if (!ignore && p?.slug) setTrending({ title: p.title, slug: p.slug, imageUrl: p.imageUrl, locale: 'ml' });
          } catch (_) { /* fall back */ }
        }
        if (!box2Slug) {
          try {
            const t = await apiGet('/api/posts', { locale: language, category: 'trending', isAd: 'false', limit: 1 });
            if (!ignore && t?.items?.length) {
              const it = t.items[0];
              setTrending({ title: it.title, slug: it.slug, imageUrl: it.imageUrl });
            } else if (!ignore) {
              setTrending(null);
            }
          } catch (_) { if (!ignore) setTrending(null); }
        }

        // Resolve Box 3 (Recent/Custom)
        if (box3Slug) {
          try {
            // Force Malayalam for admin-selected hero boxes
            const p = await apiGet(`/api/posts/${encodeURIComponent(box3Slug)}`, { locale: 'ml' });
            if (!ignore && p?.slug) setRecent({ title: p.title, slug: p.slug, imageUrl: p.imageUrl, locale: 'ml' });
          } catch (_) { /* fall back */ }
        }
        if (!box3Slug) {
          try {
            const r = await apiGet('/api/posts', { locale: language, isAd: 'false', limit: 1 });
            if (!ignore && r?.items?.length) {
              const it = r.items[0];
              setRecent({ title: it.title, slug: it.slug, imageUrl: it.imageUrl });
            } else if (!ignore) {
              setRecent(null);
            }
          } catch (_) { if (!ignore) setRecent(null); }
        }
      } catch (_) {
        // If settings fail, fallback to trending/recent
        try {
          const t = await apiGet('/api/posts', { locale: language, category: 'trending', isAd: 'false', limit: 1 });
          if (!ignore && t?.items?.length) setTrending({ title: t.items[0].title, slug: t.items[0].slug, imageUrl: t.items[0].imageUrl });
        } catch (_) { if (!ignore) setTrending(null); }
        try {
          const r = await apiGet('/api/posts', { locale: language, isAd: 'false', limit: 1 });
          if (!ignore && r?.items?.length) setRecent({ title: r.items[0].title, slug: r.items[0].slug, imageUrl: r.items[0].imageUrl });
        } catch (_) { if (!ignore) setRecent(null); }
      }
    })();
    return () => { ignore = true; };
  }, [language]);
  
  const content = {
    en: {
      title: 'Purayidakrishi',
      subtitle: 'Homestead Agroecology',
      discover: 'Discover more:',
      boxes: ['പുരയിട കൃഷി', 'മണ്ണ് സംരക്ഷണം', 'കൃഷിയിലെ ഞാറ്റുവേലകൾ']
    },
    ml: {
      title: 'പുരയിടകൃഷി',
      subtitle: 'Homestead Agroecology',
      discover: 'കൂടുതൽ അറിയുക:',
      boxes: ['പുരയിട കൃഷി', 'മണ്ണ് സംരക്ഷണം', 'കൃഷിയിലെ ഞാറ്റുവേലകൾ']
    }
  };

  return (
    <section id="home" className="hero" style={{'--hero-bg-image': `url(${bgImage})`}}>
      {/* Top left corner logo */}
      <div className="hero-corner-logo">
        <img src="/purayida-krishi-logo--white.png" alt="Purayidakrishi" className="corner-logo" />
      </div>
      
      <div className="container hero-stack">
        {/* Logo only (title and subtitle removed as requested) */}
        <div className="hero-brand">
          <img src={logoImg} alt="Purayidakrishi" className="hero-logo" />
        </div>

        {/* Three decorative circles */}
        <div className="hero-circles" aria-hidden="false">
          <span className="circle"><span className="circle-text">പുരയിടകൃഷി പ്രായോഗിക പാഠങ്ങൾ</span></span>
          <span className="circle"><span className="circle-text">കാർഷിക ലേഖനങ്ങൾ</span></span>
          <span className="circle"><span className="circle-text">സേവനങ്ങൾ</span></span>
        </div>

        {/* Discover more section with 3 boxes */}
        <div className="discover">
          <h3 className="discover-title">{content[language].discover}</h3>
          <div className="discover-grid">
            {/* Static first box with mock thumbnail (agro card layout) */}
            <a href="#/practical/1" className="discover-card">
              <div className="discover-card-inner">
                <div className="discover-thumb-16x9">
                  <img src="/agrobg.jpg" alt="Discover Purayida" loading="lazy" />
                  <div className="discover-badge">post</div>
                </div>
                <div className="discover-meta">
                  <h3>{content[language].boxes[0]}</h3>
                  <p></p>
                  <button className="discover-readmore">
                    Read More
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
                  </button>
                </div>
              </div>
            </a>
            {/* Dynamic: Trending */}
            {trending ? (
              <a href={`#/blog/${encodeURIComponent(trending.locale || language)}/${encodeURIComponent(trending.slug)}`} className="discover-card">
                <div className="discover-card-inner">
                  <div className="discover-thumb-16x9">
                    <img src={trending.imageUrl || '/agrobg.jpg'} alt={trending.title} loading="lazy" />
                    <div className="discover-badge">post</div>
                  </div>
                  <div className="discover-meta">
                    <h3>{trending.title}</h3>
                    <p></p>
                    <button className="discover-readmore">
                      Read More
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
                    </button>
                  </div>
                </div>
              </a>
            ) : (
              <a href="#/blog" className="discover-card">
                <div className="discover-card-inner">
                  <div className="discover-thumb-16x9">
                    <img src="/agrobg.jpg" alt="Trending" loading="lazy" />
                    <div className="discover-badge">post</div>
                  </div>
                  <div className="discover-meta">
                    <h3>{content[language].boxes[1]}</h3>
                    <p></p>
                    <button className="discover-readmore">
                      Read More
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
                    </button>
                  </div>
                </div>
              </a>
            )}
            {/* Dynamic: Recent */}
            {recent ? (
              <a href={`#/blog/${encodeURIComponent(recent.locale || language)}/${encodeURIComponent(recent.slug)}`} className="discover-card">
                <div className="discover-card-inner">
                  <div className="discover-thumb-16x9">
                    <img src={recent.imageUrl || '/agrobg.jpg'} alt={recent.title} loading="lazy" />
                    <div className="discover-badge">post</div>
                  </div>
                  <div className="discover-meta">
                    <h3>{recent.title}</h3>
                    <p></p>
                    <button className="discover-readmore">
                      Read More
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
                    </button>
                  </div>
                </div>
              </a>
            ) : (
              <a href="#/blog" className="discover-card">
                <div className="discover-card-inner">
                  <div className="discover-thumb-16x9">
                    <img src="/agrobg.jpg" alt="Recent" loading="lazy" />
                    <div className="discover-badge">post</div>
                  </div>
                  <div className="discover-meta">
                    <h3>{content[language].boxes[2]}</h3>
                    <p></p>
                    <button className="discover-readmore">
                      Read More
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
                    </button>
                  </div>
                </div>
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
